import { useRef, useState, useEffect } from "react";
import { parse, isValid } from "date-fns";

const pad2 = (value) => String(value).padStart(2, "0");

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "string") {
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (isValid(date)) return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()}`;
  return "";
};

const maskDigits = (rawValue, appendSeparator) => {
  const digits = rawValue.replace(/\D/g, "").slice(0, 8);
  let masked = digits.slice(0, 2);
  if (digits.length > 2 || (digits.length === 2 && appendSeparator)) masked += `/${digits.slice(2, 4)}`;
  if (digits.length > 4 || (digits.length === 4 && appendSeparator)) masked += `/${digits.slice(4, 8)}`;
  return masked;
};

const caretAfterDigits = (masked, digitCount) => {
  if (digitCount <= 0) return 0;
  let seen = 0;
  for (let index = 0; index < masked.length; index += 1) {
    if (masked[index] >= "0" && masked[index] <= "9") {
      seen += 1;
      if (seen === digitCount) {
        let position = index + 1;
        while (position < masked.length && masked[position] === "/") position += 1;
        return position;
      }
    }
  }
  return masked.length;
};

const DateInput = ({ value, onChange, placeholder = "", className = "", isInError = false, ...rest }) => {
  const inputRef = useRef(null);
  const [inputValue, setInputValue] = useState(formatValue(value));

  useEffect(() => {
    if (value === inputValue || !value) return;
    setInputValue(formatValue(value));
  }, [value]);

  const onInputChange = (event) => {
    const inputElement = event.target;
    const rawValue = inputElement.value;
    const caret = typeof inputElement.selectionStart === "number" ? inputElement.selectionStart : rawValue.length;
    const digitsBeforeCaret = rawValue.slice(0, caret).replace(/\D/g, "").length;

    const maskedValue = maskDigits(rawValue, rawValue.length >= inputValue.length);
    const nextCaret = caretAfterDigits(maskedValue, digitsBeforeCaret);

    setInputValue(maskedValue);
    inputElement.value = maskedValue;
    inputElement.setSelectionRange(nextCaret, nextCaret);

    if (maskedValue.length !== 10) return onChange(null);
    if (isValid(parse(maskedValue, "dd/MM/yyyy", new Date())) === true) {
      onChange(`${maskedValue.slice(6, 10)}-${maskedValue.slice(3, 5)}-${maskedValue.slice(0, 2)}`);
      return;
    }
  };

  return (
    <div className="relative">
      <input
        id={rest.id}
        ref={inputRef}
        type="text"
        inputMode="numeric"
        maxLength={10}
        placeholder={"JJ/MM/AAAA"}
        className={`${className} ${isInError ? "border-red-500" : "border-gray-300"}
          block w-full px-3 py-2 bg-white border rounded-md focus:outline-none focus:ring-primary focus:border-primary
          peer disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-100 h-[36px] `}
        value={inputValue}
        onChange={onInputChange}
        {...rest}
      />
      {placeholder && (
        <label
          htmlFor={rest.id}
          className={`absolute left-2 -top-2.5 px-1 text-xs transition-all
            peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400
            peer-placeholder-shown:top-2 peer-focus:-top-2.5 peer-focus:text-xs
          peer-focus:text-primary bg-white ${isInError ? "text-red-500" : "text-gray-600"}
            pointer-events-none`}
        >
          {placeholder}
        </label>
      )}
    </div>
  );
};

export default DateInput;
